import React, { useState, useEffect } from 'react';

const AccessControlModal = ({
  isVisible,
  onClose,
  isPrivate,
  onTogglePrivacy,
  viewers,
  onAddViewer,
  onRemoveViewer,
  ownerEmail,
  isDarkMode
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isPrivacyToggleInProgress, setIsPrivacyToggleInProgress] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handlePrivacyToggle = async (newIsPrivate) => {
    setIsPrivacyToggleInProgress(true);
    try {
      await onTogglePrivacy(newIsPrivate);
    } catch (error) {
      console.error('Failed to toggle privacy:', error);
    } finally {
      setIsPrivacyToggleInProgress(false);
    }
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    if (email.toLowerCase() === ownerEmail?.toLowerCase()) {
      return 'Cannot add owner as viewer';
    }
    if (viewers.some(v => v.email.toLowerCase() === email.toLowerCase())) {
      return 'This email is already added';
    }
    return null;
  };

  const handleAddViewer = () => {
    const error = validateEmail(emailInput);
    if (error) {
      setEmailError(error);
      return;
    }

    onAddViewer(emailInput.trim());
    setEmailInput('');
    setEmailError('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddViewer();
    }
  };

  const handleClose = () => {
    setEmailInput('');
    setEmailError('');
    onClose();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isVisible) {
      setEmailInput('');
      setEmailError('');
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002,
      padding: '20px'
    }}>
      <div style={{
        background: isDarkMode ? '#1f2937' : '#ffffff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '480px',
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        animation: 'modalSlideIn 0.2s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 0 24px',
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: isDarkMode ? '#f9fafb' : '#111827'
          }}>
            Manage Access
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0
        }}>
          {/* Privacy Toggle Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: isDarkMode ? '#f3f4f6' : '#374151'
            }}>
              Who can access this summary?
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Public Option */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                backgroundColor: !isPrivate ? (isDarkMode ? '#374151' : '#f3f4f6') : 'transparent'
              }}>
                <input
                  type="radio"
                  name="privacy"
                  checked={!isPrivate}
                  onChange={() => handlePrivacyToggle(false)}
                  disabled={isPrivacyToggleInProgress}
                  style={{
                    margin: '2px 0 0 0',
                    accentColor: isDarkMode ? '#3b82f6' : '#2563eb'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isDarkMode ? '#f9fafb' : '#111827',
                    marginBottom: '4px'
                  }}>
                    Public
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: isDarkMode ? '#d1d5db' : '#6b7280'
                  }}>
                    Anyone with the link can view
                  </div>
                </div>
              </label>

              {/* Private Option */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                backgroundColor: isPrivate ? (isDarkMode ? '#374151' : '#f3f4f6') : 'transparent'
              }}>
                <input
                  type="radio"
                  name="privacy"
                  checked={isPrivate}
                  onChange={() => handlePrivacyToggle(true)}
                  disabled={isPrivacyToggleInProgress}
                  style={{
                    margin: '2px 0 0 0',
                    accentColor: isDarkMode ? '#3b82f6' : '#2563eb'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isDarkMode ? '#f9fafb' : '#111827',
                    marginBottom: '4px'
                  }}>
                    Private
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: isDarkMode ? '#d1d5db' : '#6b7280'
                  }}>
                    Only people you add can view
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Divider */}
          <div style={{
            height: '1px',
            backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
            margin: '24px 0'
          }} />

          {/* Add People Section */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: isDarkMode ? '#f3f4f6' : '#374151'
            }}>
              Add people
            </h3>
            
            {/* Email Notification Info */}
            <div style={{
              padding: '8px 12px',
              backgroundColor: isDarkMode ? '#374151' : '#f0f9ff',
              border: `1px solid ${isDarkMode ? '#4b5563' : '#bae6fd'}`,
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '12px',
              color: isDarkMode ? '#d1d5db' : '#0369a1'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Email notifications will be sent. Users may need to create an account.
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="email@example.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${emailError ? '#ef4444' : (isDarkMode ? '#4b5563' : '#d1d5db')}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
                    color: isDarkMode ? '#f9fafb' : '#111827',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = isDarkMode ? '#6b7280' : '#9ca3af';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = emailError ? '#ef4444' : (isDarkMode ? '#4b5563' : '#d1d5db');
                  }}
                />
                {emailError && (
                  <div style={{
                    fontSize: '12px',
                    color: '#ef4444',
                    marginTop: '4px'
                  }}>
                    {emailError}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddViewer}
                disabled={!emailInput.trim() || isPrivacyToggleInProgress}
                style={{
                  padding: '12px 16px',
                  backgroundColor: emailInput.trim() ? (isDarkMode ? '#3b82f6' : '#2563eb') : (isDarkMode ? '#4b5563' : '#d1d5db'),
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: emailInput.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (emailInput.trim()) {
                    e.target.style.backgroundColor = isDarkMode ? '#2563eb' : '#1d4ed8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (emailInput.trim()) {
                    e.target.style.backgroundColor = isDarkMode ? '#3b82f6' : '#2563eb';
                  }
                }}
              >
                Add viewer
              </button>
            </div>
          </div>

          {/* People with Access Section */}
          <div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: isDarkMode ? '#f3f4f6' : '#374151'
            }}>
              People with access
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Owner */}
              {ownerEmail && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDarkMode ? '#f9fafb' : '#111827',
                    fontWeight: '500'
                  }}>
                    {ownerEmail} (Owner)
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    Owner
                  </div>
                </div>
              )}

              {/* Viewers */}
              {viewers.length > 0 ? (
                viewers.map((viewer, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`
                    }}
                  >
                    <div style={{
                      fontSize: '14px',
                      color: isDarkMode ? '#f9fafb' : '#111827'
                    }}>
                      {viewer.email}
                    </div>
                    <button
                      onClick={() => onRemoveViewer(viewer.email)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        fontSize: '16px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? '#4b5563' : '#e5e7eb';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = isDarkMode ? '#9ca3af' : '#6b7280';
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  fontSize: '14px',
                  backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  No viewers added yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px 24px',
          borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '12px 24px',
              backgroundColor: isDarkMode ? '#374151' : '#111827',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#4b5563' : '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#374151' : '#111827';
            }}
          >
            Done
          </button>
        </div>
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AccessControlModal;
